/**
 * Hook personalizado para gerenciar o download de arquivos
 *
 * Este hook fornece uma interface simples para gerenciar o download de arquivos
 * a partir de respostas de API, especialmente quando eles são retornados como ArrayBuffer.
 *
 * @example
 * const { isDownloading, startDownload } = useFileDownloader({
 *   defaultFileName: 'arquivo.zip',
 *   onSuccess: () => console.log('Download concluído!'),
 *   onError: (err) => console.error('Erro no download:', err)
 * });
 *
 * // Para iniciar o download:
 * const handleDownload = async () => {
 *   const response = await api.get('/download/file', { responseType: 'arraybuffer' });
 *   if (response.data) {
 *     await startDownload(response);
 *   }
 * };
 */
import { useState } from "react";

interface UseFileDownloaderOptions {
  defaultFileName?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}
export function useFileDownloader({
  defaultFileName = "download.zip",
  onSuccess,
  onError
}: UseFileDownloaderOptions = {}) {
  const [isDownloading, setIsDownloading] = useState(false);

  const extractFileName = (fileNameHeader?: string): string => {
    if (!fileNameHeader) return defaultFileName;

    const parts = fileNameHeader.split("filename=");

    if (parts.length > 1) {
      return parts[1].split(";")[0].replace(/"/g, "");
    }

    return defaultFileName;
  };

  const downloadFile = (
    arrayBuffer: ArrayBuffer,
    options: {
      contentType?: string;
      fileNameHeader?: string;
      fileName?: string;
    } = {}
  ) => {
    try {
      const {
        contentType = "application/octet-stream",
        fileNameHeader = null,
        fileName
      } = options;

      const blob = new Blob([arrayBuffer], { type: contentType });

      const downloadFileName =
        fileName || extractFileName(fileNameHeader) || defaultFileName;

      const objectUrl = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = objectUrl;
      a.download = downloadFileName;
      window.document.body.appendChild(a);
      a.click();

      window.setTimeout(() => {
        window.document.body.removeChild(a);
        window.URL.revokeObjectURL(objectUrl);

        if (onSuccess) onSuccess();
      }, 100);

      return true;
    } catch (error) {
      if (onError)
        onError(
          error instanceof Error
            ? error
            : new Error("Erro ao fazer download do arquivo")
        );
      console.error("Erro ao fazer download do arquivo:", error);

      return false;
    }
  };

  const startDownload = <
    T extends {
      data: ArrayBuffer;
      response?: { headers?: { get: (key: string) => string | null } };
    }
  >(
    response: T,
    options: {
      fileName?: string;
      contentType?: string;
    } = {}
  ) => {
    setIsDownloading(true);

    try {
      const contentType =
        options.contentType ||
        response.response?.headers?.get("content-type") ||
        "application/octet-stream";
      const fileNameHeader = response.response?.headers?.get(
        "content-disposition"
      );

      const success = downloadFile(response.data, {
        contentType,
        fileNameHeader,
        fileName: options.fileName
      });

      return success;
    } catch (error) {
      if (onError)
        onError(
          error instanceof Error
            ? error
            : new Error("Erro no download do arquivo")
        );

      return false;
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    isDownloading,
    startDownload,
    downloadFile
  };
}
